import Vue from 'vue'
import { $_bridge } from '@/nms/plugins/bridge.js'
import merge from 'deepmerge'

// Типы для фильтра
interface FilterState {
  channelTypes: any[]
  locationTypes: any[]
  objectTypes: string[]
  locationSubtypes: any[]
}

// Типы для ресурсов
interface LogicalResource {
  name: string
  resources: any[]
}

interface SelectedChannel {
  channel: any
  $id: string
  name: string
  cls: string
  connections: any[]
  show: boolean
}

// Типы для состояния
interface TopologicalState {
  connections: any[]
  channels: any[]
  areas: any[]
  selectedChannels: SelectedChannel[]
  selectedConnections: any[]
  connectionsShow: boolean
  areasShow: boolean
  logicalGroups: any[]
  logicalResources: LogicalResource[]
  topologySidebar: number
  filter: FilterState
  mapSourceOnline: boolean
}

// Начальное состояние
const getDefaultState = (): TopologicalState => {
  return {
    connections: [],
    channels: [],
    areas: [],
    selectedChannels: [],
    selectedConnections: [],
    connectionsShow: false,
    areasShow: false,
    logicalGroups: [],
    logicalResources: [],
    topologySidebar: 1,
    filter: {
      channelTypes: [],
      locationTypes: [],
      objectTypes: ['facility', 'building'],
      locationSubtypes: [],
    },
    mapSourceOnline: false
  }
}

const state: TopologicalState = getDefaultState()

export default {
  state,

  mutations: {
    LOAD_CONNECTIONS(state: TopologicalState, data: any[]) {
      state.connections = data
    },

    LOAD_CHANNELS(state: TopologicalState, data: any[]) {
      state.channels = data
    },
    
    LOAD_AREAS(state: TopologicalState, data: any[]) {
      state.areas = data
    },

    LOAD_LOGICAL_GROUPS(state: TopologicalState, data: any[]) {
      state.logicalGroups = data
    },

    ADD_LOGICAL_RESOURCES(state: TopologicalState, data: LogicalResource) {
      const f = state.logicalResources.findIndex(item => {
        return item.name === data.name
      })
      if(f < 0) {
        state.logicalResources.push(data)
      } else {
        Vue.set(state.logicalResources, f, data)
      }
    },

    ADD_SELECTED_CHANNELS(state: TopologicalState, data: SelectedChannel[]) {
      state.selectedChannels = data
    },
    
    ADD_SELECTED_CONNECTIONS(state: TopologicalState, data: any[]) {
      state.selectedConnections = data
    },

    SHOW_CONNECTIONS(state: TopologicalState, data: boolean) {
      state.connectionsShow = data
    },
    
    APPLY_TOPOLOGY_FILTER(state: TopologicalState, data: FilterState) {
      Vue.set(state, 'filter', data)
    },
    
    SHOW_AREAS(state: TopologicalState, data: boolean) {
      state.areasShow = data
    },

    CLEAR_CONNECTIONS(state: TopologicalState) {
      state.connections = []
    },

    CLEAR_LOGICAL_RESOURCES(state: TopologicalState) {
      state.logicalResources = []
    },

    CLEAR_CHANNELS(state: TopologicalState) {
      state.channels.forEach((item) => {
        item.showOnMap = false
      })
    },

    CHANGE_TOPOLOGY_SIDEBAR(state: TopologicalState, data: number) {
      state.topologySidebar = data
    },
    
    CHANGE_MAPSOURCEONLINE(state: TopologicalState, data: boolean) {
      state.mapSourceOnline = data
    },
  },

  actions: {
    async loadConnections({ commit }: any) {
      try {
        const data = {
          boundCls: "device"
        }
        const connections = await $_bridge.customQuery('cmdb', 'services/connections/external', data, 'POST')
        if(connections?.length) {
          $_bridge.subscribe(connections.map((obj: any) => obj.$id))
          commit('LOAD_CONNECTIONS', connections)
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'loadConnections', err)
      }
    },

    async loadChannels({ commit, state }: any) {
      try {
        const channels = await $_bridge.getObjectsByClass('channel')
        if(channels) {
          const selectedChannels: SelectedChannel[] = []
          
          for(const channel of channels) {
            channel.showOnMap = false
            const resources = await channel.getRefs()
            
            if(resources?.length) {
              channel.resources = []
              resources.forEach((item: any) => {
                if(item.cls === 'connection' && !channel.resources.includes(item)) {
                  const connection = state.connections.find((conn: any) => conn.$id === item.$id)
                  if(connection) {
                    const c = merge({}, connection)
                    channel.resources.push(c)
                  }
                }
              })
            }
            
            selectedChannels.push({
              channel: channel,
              $id: channel.$id,
              name: channel.name,
              cls: channel.cls,
              connections: channel.resources,
              show: channel.showOnMap
            })
          }
          
          console.log('STORE', 'topology', 'loadChannels', channels)
          $_bridge.subscribe(channels.map((obj: any) => obj.$id))
          commit('LOAD_CHANNELS', channels)
          commit('ADD_SELECTED_CHANNELS', selectedChannels)
        } else {
          console.log('STORE', 'loadChannels', 'smth wrong')
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'loadChannels', err)
      }
    },

    async loadLogicalGroups({ commit }: any) {
      try {
        const query = {
          id: ['/logical'],
          notNull: true,
          data: {
            namePath: true,
            links: {
              flatten: false,
            }
          }
        }
        const logical = await $_bridge.cmdbQuery(query)
        const logic = logical[0].links
        console.log('STORE', 'logic', logic)
        commit('LOAD_LOGICAL_GROUPS', logic)
      } catch(err) {
        console.error('CATCH', 'STORE', 'LOAD_LOGICAL_GROUPS', err)
      }
    },

    async loadLogicalResources({ commit }: any, filter: { types: any[] }) {
      try {
        if(filter.types?.length) {
          console.log('STORE', 'loadLogicalResources', 'filter.types', filter.types)
          
          for(const type of filter.types) {
            if(type.children?.length) {
              const childrenResources: any[] = []
              
              for(const child of type.children) {
                const resources = await child.getRefs()
                if(resources?.length) {
                  const filteredResources: any[] = []
                  resources.forEach((item: any) => {
                    if(item.cls === 'connection' && !filteredResources.includes(item)) {
                      filteredResources.push(item)
                    }
                  })
                  
                  if(filteredResources.length) {
                    childrenResources.push({
                      name: child.name,
                      object: child,
                      resources: filteredResources
                    })
                  }
                }
              }
              
              if(childrenResources.length) {
                console.log('STORE', 'LOAD_LOGICAL_RESOURCES', 'resources', type.name, childrenResources)
                commit('ADD_LOGICAL_RESOURCES', {
                  name: type.name,
                  resources: childrenResources
                })
              }
            }
          }
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'LOAD_LOGICAL_RESOURCES', err)
      }
    },

    async showSelectedConnections({ commit, state }: any, connections: any[]) {
      try {
        const selectedConnections: any[] = []
        
        if(connections?.length) {
          state.connections.forEach((connection: any) => {
            if(connections.some((item: any) => connection.$id === item.$id)) {
              connection.show = true
              selectedConnections.push(connection)
            } else {
              connection.show = false
            }
          })
        } else {
          state.connections.forEach((connection: any) => {
            connection.show = false
          })
        }
        
        commit('ADD_SELECTED_CONNECTIONS', selectedConnections)
      } catch(err) {
        console.error('CATCH', 'STORE', 'ADD_SELECTED_CONNECTIONS', err)
      }
    },
    
    async showSelectedChannels({ commit, state }: any, channels: any[]) {
      console.log('STORE', 'SHOW_SELECTED_CHANNELS', 'resources', channels)
      console.log('STORE', 'SHOW_SELECTED_CHANNELS', 'state.selectedChannels', state.selectedChannels)
      
      try {
        const selectedChannels: SelectedChannel[] = []
        
        if(channels?.length) {
          state.selectedChannels.forEach((channel: SelectedChannel, i: number) => {
            if(channels.some((item: any) => channel.$id === item.$id)) {
              channel.show = true
            } else {
              channel.show = false
            }
            selectedChannels[i] = channel
          })
        } else {
          state.selectedChannels.forEach((channel: SelectedChannel, i: number) => {
            channel.show = false
            selectedChannels[i] = channel
          })
        }
        
        console.log('STORE', 'SHOW_SELECTED_CHANNELS', 'selectedChannels$', selectedChannels)
        commit('ADD_SELECTED_CHANNELS', selectedChannels)
      } catch(err) {
        console.error('CATCH', 'STORE', 'SHOW_SELECTED_CHANNELS', err)
      }
    },

    async loadAreas({ commit }: any) {
      try {
        const locations = await $_bridge.getObjectsByClass('location')
        const areas = locations.filter((location: any) => location.area)
        console.log('STORE', 'loadAreas', 'areas', areas)
        
        if(areas.length) {
          commit('LOAD_AREAS', areas)
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'loadAreas', 'err', err)
      }
    },

    applyTopologyFilter({ commit }: any, data: FilterState) {
      console.log('STORE', 'applyTopologyFilter', 'data', data)
      commit('APPLY_TOPOLOGY_FILTER', data)
    },
    
    showAreas({ commit }: any, data: boolean) {
      commit('SHOW_AREAS', data)
    },
    
    showConnections({ commit }: any, data: boolean) {
      commit('SHOW_CONNECTIONS', data)
    },

    clearConnections({ commit }: any) {
      commit('CLEAR_CONNECTIONS')
    },

    clearLogicalResources({ commit }: any) {
      commit('CLEAR_LOGICAL_RESOURCES')
    },

    changeTopologySidebar({ commit }: any, data: number) {
      commit('CHANGE_TOPOLOGY_SIDEBAR', data)
    },

    async checkMapSource({ commit }: any) {
      try {
        const query = {
          id: ['/config/server'],
        }
        const server = await $_bridge.cmdbQuery(query)
        
        if(server[0]?.config?.hasOwnProperty('OSM')) {       
          commit('CHANGE_MAPSOURCEONLINE', true)
        }
      } catch(err) {
        console.error('CATCH', 'STORE', 'checkMapSource', err)
      }
    },
  },

  getters: {
    getConnections(state: TopologicalState): any[] {
      return state.connections
    },
    
    channels(state: TopologicalState): any[] {
      return state.channels
    },
  },
}